// Feature: features/nma-dx-coding-guidelines.md
import React from 'react';
import {
  Box,
  Heading,
  Text,
  Alert,
  VStack,
  HStack,
  Badge,
  Link,
  Divider,
  List,
  ListItem,
  useColorModeValue,
} from '@chakra-ui/react';
import { ExternalLinkIcon } from '@chakra-ui/icons';

const RUBRIC_ROWS = [
  {
    score: '+100',
    colorScheme: 'green',
    description: 'Primary surgical indication / reason for the procedure.',
  },
  {
    score: '+80',
    colorScheme: 'green',
    description:
      'Explicitly listed as covered in the applicable NCD/LCD/Article or Commercial Policy for the billed IONM code.',
  },
  {
    score: '+60',
    colorScheme: 'teal',
    description: 'Neurologic or anatomic risk directly tied to the operative field.',
  },
  {
    score: '+40',
    colorScheme: 'blue',
    description: 'Active comorbidity increasing neurologic risk or justifying IONM.',
  },
  {
    score: '+20 / -20',
    colorScheme: 'purple',
    description:
      'Symptom/status adjustments (note: neurological and musculoskeletal symptoms required to justify IONM/EMG—such as pain, weakness, numbness, tingling, gait instability, and radiculopathy—are protected and scored positively as IONM Support).',
  },
  {
    score: '-100',
    colorScheme: 'red',
    description:
      'Invalid, rule-out only, unrelated systemic comorbidity (e.g., hypertension or diabetes noted for anesthesia clearance), or anatomical mismatch (e.g., a lumbar diagnosis on a cervical procedure).',
  },
  {
    score: '-1000',
    colorScheme: 'red',
    description: 'Duplicative and less specific than another diagnosis present on the case.',
  },
];

const NMADxCodingLogic = () => {
  const sectionBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  return (
    <Box maxW="900px">
      <Heading as="h1" size="xl" mb={6} color="brand.700">
        NMA DX Code Logic
      </Heading>

      <VStack spacing={10} align="stretch">
        {/* Section 1 */}
        <Box>
          <Heading as="h2" size="lg" mb={4} color="brand.800">
            1. ICD-10 Code Selection &amp; Scoring Logic
          </Heading>

          <VStack spacing={4} align="stretch">
            <Box>
              <Text mb={2}>
                <strong>Policy Hierarchy:</strong> Codes are evaluated against applicable Commercial Payer Policies and the Medicare policy hierarchy for the case’s MAC jurisdiction (<code>NCD -&gt; MAC-specific LCD -&gt; Billing &amp; Coding Article</code>).
              </Text>
            </Box>

            <Alert
              status="info"
              variant="left-accent"
              flexDirection="column"
              alignItems="flex-start"
              bg={sectionBg}
              borderColor="brand.500"
              p={5}
              borderRadius="md"
            >
              <Text fontWeight="bold" mb={3}>
                NMA Classification &amp; Scoring Rubric provided by NMA:
              </Text>
              <Text fontSize="sm" color="gray.600" mb={4}>
                Each diagnosis is classified and scored (scores stack if multiple criteria apply; only codes with a net score <strong>&gt; 0</strong> are retained):
              </Text>

              <VStack spacing={3} align="stretch" w="100%">
                {RUBRIC_ROWS.map((row) => (
                  <HStack key={row.score} align="flex-start" spacing={3}>
                    <Badge
                      colorScheme={row.colorScheme}
                      fontSize="sm"
                      px={2}
                      py={0.5}
                      minW="80px"
                      textAlign="center"
                      flexShrink={0}
                    >
                      {row.score}
                    </Badge>
                    <Text fontSize="sm">{row.description}</Text>
                  </HStack>
                ))}
              </VStack>
            </Alert>

            <Box>
              <Text fontWeight="bold" mb={1}>
                Specificity &amp; CDC Excludes Rules:
              </Text>
              <List spacing={2} pl={4} styleType="disc">
                <ListItem>
                  <strong>CDC Excludes1 / Excludes2:</strong> Surviving codes are checked against official CDC Excludes rules so mutually exclusive codes are not billed together.
                </ListItem>
              </List>
            </Box>
          </VStack>
        </Box>

        <Divider borderColor={borderColor} />

        {/* Section 2 */}
        <Box>
          <Heading as="h2" size="lg" mb={4} color="brand.800">
            2. Diagnosis Extraction from Clinical Documentation (Mode 2)
          </Heading>

          <VStack spacing={4} align="stretch">
            <Text>
              <strong>We follow Mode 2 of our ICD-10 Coding Modes</strong> (
              <Link color="blue.500" href="https://revedy.io/icd10-coding-modes" isExternal>
                https://revedy.io/icd10-coding-modes <ExternalLinkIcon mx="2px" />
              </Link>
              ).
            </Text>

            <Alert
              status="info"
              variant="left-accent"
              flexDirection="column"
              alignItems="flex-start"
              bg={sectionBg}
              borderColor="brand.500"
              p={5}
              borderRadius="md"
            >
              <Text mb={3}>
                <strong>How Mode 2 Works:</strong> The engine assigns codes for diagnoses explicitly named in the documentation—whether written as code strings or as diagnostic language—and resolves them to the highest specificity supported by the chart. Per ICD-10-CM Official Guidelines (§IV.D &amp; §IV.H for outpatient and professional fee claims) and standard certified coding practice, the system also codes documented signs, symptoms, and abnormal findings that establish medical necessity, while excluding uncertain diagnoses (<em>&quot;probable,&quot; &quot;suspected,&quot; &quot;questionable,&quot;</em> or <em>&quot;rule out&quot;</em>).
              </Text>
              <Divider my={2} borderColor={borderColor} />
              <Text>
                <strong>Coder Visibility:</strong> When a diagnosis is coded from diagnostic language without the literal ICD-10 code string appearing in the hospital documentation, it is highlighted in yellow on the Summary Report for review.
              </Text>
            </Alert>
          </VStack>
        </Box>

        <Divider borderColor={borderColor} />

        {/* Section 3 */}
        <Box>
          <Heading as="h2" size="lg" mb={4} color="brand.800">
            3. Diagnosis Sequencing &amp; CPT Pointer Assignment
          </Heading>

          <VStack spacing={4} align="stretch">
            <Box p={4} bg={sectionBg} borderRadius="md" border="1px solid" borderColor={borderColor}>
              <Text fontWeight="bold" mb={2}>
                Box 21 Diagnosis Sequence (Pointers A–F, max 6):
              </Text>
              <List spacing={2} pl={4} styleType="disc">
                <ListItem>
                  <strong>Pointer A</strong> is always assigned to the primary surgical indication (highest-scoring diagnosis driving the procedure).
                </ListItem>
                <ListItem>
                  <strong>Pointers B, C, D...</strong> are ordered by clinical importance and rubric score.
                </ListItem>
              </List>
            </Box>

            <Box p={4} bg={sectionBg} borderRadius="md" border="1px solid" borderColor={borderColor}>
              <Text fontWeight="bold" mb={2}>
                Box 24E Per-CPT Pointer Assignment (max 4 per CPT):
              </Text>
              <List spacing={2} pl={4} styleType="disc">
                <ListItem>
                  <strong>Coverage Crosswalk:</strong> Diagnoses explicitly covered for that specific CPT code under the applicable LCD/Article are prioritized first.
                </ListItem>
                <ListItem>
                  <strong>Clinical Justification:</strong> The primary surgical indication (Pointer A) and modality-specific symptoms justifying that service are linked up to the 4-pointer CMS-1500/837P limit.
                </ListItem>
              </List>
            </Box>
          </VStack>
        </Box>

        <Divider borderColor={borderColor} />

        {/* Section 4 */}
        <Box>
          <Heading as="h2" size="lg" mb={4} color="brand.800">
            4. Excluding Unlinked Diagnosis Codes
          </Heading>

          <Box p={5} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200">
            <VStack spacing={3} align="stretch" color="blue.900">
              <Text>
                <strong>Rule:</strong> Every diagnosis included in the Box 21 DX list must be assigned as a pointer on at least one billed CPT code (across both Professional and Technical claims).
              </Text>
              <Text>
                <strong>Behavior:</strong> Any secondary diagnosis not used as a pointer on at least one CPT line is removed from the DX list, and the remaining pointers are re-indexed contiguously (<strong>A, B, C...</strong>) with no gaps.
              </Text>
            </VStack>
          </Box>
        </Box>
      </VStack>
    </Box>
  );
};

export default NMADxCodingLogic;
